import * as React from "react";
import { EmojiClickData } from "emoji-picker-react";
import { MouseDownEvent } from "emoji-picker-react/dist/config/config";
import {
    Modal,
    Button,
    Dropdown,
    Icon,
    TextArea,
    Tooltip,
} from "ui/components";
import useOnClickOutside from "ui/hooks/useOnClickOutside";
import { usePublishContext } from "ui/context/PublishContext";
import { Fragment, SubtitleExportValue } from "core/models";
import subtitleExportOptions from "core/constants/captionsExport";
import styles from "./ShareTwitterModal.module.scss";

const Picker = React.lazy(() => import("emoji-picker-react"));

interface Props {
    fragment: Fragment;
    handleClose(): void;
    handleShare(
        selectedAccount: string,
        tweet: string,
        subtitleExport: SubtitleExportValue,
    ): void;
}

const ShareTwitterModal: React.FC<Props> = ({
    handleClose,
    handleShare,
    fragment,
}: Props) => {
    const publishContext = usePublishContext();
    const twitterAccounts = publishContext.activeTwitterAccounts.map(
        (account) => ({
            id: account.key,
            title: account.name,
            value: account.key,
        }),
    );
    const [subtitleOption, setSubtitleOption] =
        React.useState<SubtitleExportValue>();
    const [selectedAccount, setSelectedAccount] = React.useState<
        string | undefined
    >(twitterAccounts[0]?.value);
    const [tweet, setTweet] = React.useState<string>("");
    const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
    const emojiElRef = React.useRef<HTMLDivElement | null>(null);

    const updateText = (newText: string) => {
        setTweet(newText.substring(0, 280));
        setTweet(newText.replace("@", ""));
    };

    const addEmoji: MouseDownEvent = (
        data: EmojiClickData,
        event: MouseEvent,
    ) => {
        const newTweet = `${tweet}${data.emoji}`;
        updateText(newTweet);
    };

    const handleTitleChange = (value: string) => updateText(value);

    const toggleEmojiPicker = React.useCallback(
        (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.preventDefault();
            setShowEmojiPicker((v) => !v);
        },
        [],
    );

    const closeEmojiPicker = React.useCallback(
        () => setShowEmojiPicker(false),
        [],
    );

    useOnClickOutside(emojiElRef, closeEmojiPicker);

    const handleShareClick = () => {
        if (selectedAccount) {
            handleShare(selectedAccount, tweet, subtitleOption);
        }
    };

    const handleSubtitleOptionChange = (val: SubtitleExportValue): void => {
        setSubtitleOption(val);
    };

    const modalContent = (
        <div className={styles.form}>
            <div className={styles.formelement}>
                <div className={styles.formelement__caption}>Post to</div>
                <Dropdown
                    theme="dark"
                    options={twitterAccounts}
                    onValueChanged={setSelectedAccount}
                    value={selectedAccount}
                />
            </div>

            <div className={styles.fromelement}>
                <div className={styles.twitter__publish__top}>
                    <div className={styles.formelement__caption}>
                        Write a tweet
                    </div>
                    <Tooltip
                        className={styles.twitter__textarea__tooltip}
                        text="Due to Twitter&#39;s API limitations we are unable to
                        allow and/or support mentions (@username) in tweets"
                    >
                        <Icon
                            className={styles.twitter__textarea__tooltip_icon}
                            name="info"
                        />
                    </Tooltip>
                </div>
                <TextArea
                    className={styles.twitter__textarea}
                    value={tweet}
                    onValueChanged={handleTitleChange}
                    placeholder="What do you want to share?"
                />
                <div className={styles.twitter__textarea__toolbar}>
                    <React.Suspense fallback={<div>Loading...</div>}>
                        <span className={styles.toggle__emoji}>
                            <Button
                                type="function"
                                theme="light"
                                tooltip="Add emoticons"
                                onClick={toggleEmojiPicker}
                            >
                                <Icon name="emoji" />
                            </Button>
                            <div
                                ref={emojiElRef}
                                className={styles.emoji__picker}
                                style={{
                                    display: showEmojiPicker ? "block" : "none",
                                }}
                            >
                                <Picker onEmojiClick={addEmoji} />
                            </div>
                        </span>
                    </React.Suspense>
                    <span className={styles.tweet__text__count}>
                        {`${tweet.length}/280`}
                    </span>
                </div>
                <div className={styles.formelement}>
                    <Tooltip
                        text="No captions for this clip"
                        disabled={fragment.hasSubtitles()}
                    >
                        <div className={styles.formelement__caption}>
                            Captions
                        </div>
                        <Dropdown
                            theme="dark"
                            options={subtitleExportOptions}
                            onValueChanged={handleSubtitleOptionChange}
                            value={subtitleOption}
                            disabled={!fragment.hasSubtitles()}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="twitter"
            theme="dark"
            onSave={handleShareClick}
            onClose={handleClose}
            saveButtonText="Share"
            title={`Share "${fragment.title}" to Twitter`}
            icon="twitter"
        />
    );
};

export default ShareTwitterModal;
